/**
 * Venue hot / cold holding range (one per venue, not per item).
 * Express owns the range: logs snapshot it and the day payload sends it.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS,
  FOOD_SAFETY_HOLDING_SECTIONS,
  FOOD_SAFETY_HOLDING_SUGGESTED_RANGES,
  FOOD_SAFETY_HOLDING_TIMED_SECTIONS,
  FOOD_SAFETY_SECTION_LABELS,
} from 'src/constants/foodSafety'
import { formatCoolingDuration } from 'src/utils/foodSafetyCooling'
import { formatFoodSafetyTempRange } from 'src/utils/foodSafetyTempRange'

/** Monitoring frequency belongs to the venue's documented food safety procedure. */
export const FOOD_SAFETY_HOT_HOLDING_GUIDANCE =
  'A cooking temperature shows food was cooked; it does not show that food stayed hot during holding. Take a fresh temperature reading after a meaningful period in hot holding.'
export const FOOD_SAFETY_HOT_HOLDING_CHECK_GUIDANCE =
  'Follow your venue’s food safety procedure for when to check again — for example, around 1 hour after holding starts if your procedure specifies this. Continue checking while food is held.'

export const FOOD_SAFETY_HOLDING_TEMP_LIMITS = Object.freeze({
  min: -50,
  max: 150,
})

export const FOOD_SAFETY_HOLDING_NO_RANGE_LABEL =
  'No range — readings aren’t flagged'

export function isFoodSafetyHoldingSection(section) {
  return FOOD_SAFETY_HOLDING_SECTIONS.includes(section)
}

/** Hot holding can carry an optional max hold time. */
export function isFoodSafetyHoldingTimedSection(section) {
  return FOOD_SAFETY_HOLDING_TIMED_SECTIONS.includes(section)
}

const toLimit = value => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/** "63°C or above" / "8°C or below" / "1–8°C" / no range. */
export function formatFoodSafetyHoldingRange(range) {
  if (!range || range.noRange) return FOOD_SAFETY_HOLDING_NO_RANGE_LABEL
  const min = toLimit(range.tempMin)
  const max = toLimit(range.tempMax)
  if (min != null && max != null) return formatFoodSafetyTempRange(min, max)
  if (min != null) return `${min}°C or above`
  if (max != null) return `${max}°C or below`
  return FOOD_SAFETY_HOLDING_NO_RANGE_LABEL
}

/**
 * Composer temp config from the day's `holdingRange`. Null when Express
 * didn't send one — the screen then falls back to the suggested defaults.
 */
export function holdingRangeTempConfig(range) {
  if (!range || typeof range !== 'object') return null
  if (range.noRange) return { noRange: true }
  const config = {}
  const min = toLimit(range.tempMin)
  const max = toLimit(range.tempMax)
  if (min != null) config.tempMin = min
  if (max != null) config.tempMax = max
  if (min == null && max == null) return { noRange: true }
  return config
}

/** Settings card draft from GET /settings `holdingRanges[section]`. */
export function holdingRangeDraftFromSettings(section, range) {
  const suggested = FOOD_SAFETY_HOLDING_SUGGESTED_RANGES[section] || {}
  const source = range || { ...suggested, noRange: false }
  return {
    noRange: Boolean(source.noRange),
    tempMin: source.noRange ? '' : source.tempMin ?? '',
    tempMax: source.noRange ? '' : source.tempMax ?? '',
    ...(isFoodSafetyHoldingTimedSection(section)
      ? { maxMinutes: source.maxMinutes ?? '' }
      : {}),
  }
}

/** Error string or null — mirrors the Express PATCH rules. */
export function validateFoodSafetyHoldingRangeDraft(section, draft) {
  if (draft?.noRange) return validateMaxMinutes(section, draft)
  const label = FOOD_SAFETY_SECTION_LABELS[section] || 'Holding'
  const parts = [
    ['minimum', draft?.tempMin],
    ['maximum', draft?.tempMax],
  ]
  for (const [name, raw] of parts) {
    if (raw == null || raw === '') continue
    const n = Number(raw)
    if (!Number.isFinite(n)) return `${label} ${name} must be a number.`
    if (
      n < FOOD_SAFETY_HOLDING_TEMP_LIMITS.min ||
      n > FOOD_SAFETY_HOLDING_TEMP_LIMITS.max
    ) {
      return `${label} ${name} must be between ${FOOD_SAFETY_HOLDING_TEMP_LIMITS.min} and ${FOOD_SAFETY_HOLDING_TEMP_LIMITS.max}°C.`
    }
  }
  const min = toLimit(draft?.tempMin)
  const max = toLimit(draft?.tempMax)
  if (min == null && max == null) {
    return `Set a minimum or maximum for ${label.toLowerCase()} — or choose No range.`
  }
  if (min != null && max != null && min > max) {
    return `${label}: minimum must be at or below maximum.`
  }
  return validateMaxMinutes(section, draft)
}

function validateMaxMinutes(section, draft) {
  if (!isFoodSafetyHoldingTimedSection(section)) return null
  const raw = draft?.maxMinutes
  if (raw == null || raw === '') return null
  const n = Number(raw)
  const { min, max } = FOOD_SAFETY_HOLDING_MAX_MINUTES_BOUNDS
  if (!Number.isInteger(n) || n < min || n > max) {
    return `Max hold time must be whole minutes between ${min} and ${max}.`
  }
  return null
}

/** PATCH /settings body for one holding section. */
export function buildFoodSafetyHoldingRangePatch(section, draft) {
  const range = draft?.noRange
    ? { tempMin: null, tempMax: null }
    : { tempMin: toLimit(draft?.tempMin), tempMax: toLimit(draft?.tempMax) }
  if (isFoodSafetyHoldingTimedSection(section)) {
    range.maxMinutes = toLimit(draft?.maxMinutes)
  }
  return { holdingRanges: { [section]: range } }
}

/** PATCH /settings body that goes back to the suggested range. */
export function buildFoodSafetyHoldingRangeResetPatch(section) {
  return { holdingRanges: { [section]: null } }
}

/** "max hold 2 hr" for the range line, or '' when no limit is set. */
export function formatFoodSafetyHoldingMaxTime(range) {
  const max = toLimit(range?.maxMinutes)
  return max != null ? `max hold ${formatCoolingDuration(max)}` : ''
}

/** One-tap "went into hot holding" offsets (minutes before now). */
export const FOOD_SAFETY_HELD_SINCE_CHIPS = Object.freeze([
  { minutes: 60, label: '1 hr ago' },
  { minutes: 90, label: '1½ hr ago' },
  { minutes: 120, label: '2 hr ago' },
])

/** Composer line under "Hot-held since". */
export function formatFoodSafetyHeldSinceLine(minutes, maxMinutes) {
  const max = toLimit(maxMinutes)
  const limit = max != null ? `max ${formatCoolingDuration(max)}` : ''
  const held = toLimit(minutes)
  if (held == null) {
    return limit ? `Add when it went in to check the ${limit} hold.` : ''
  }
  const over = formatFoodSafetyHeldTooLong(held, max)
  if (over) return over
  return `Held ${formatCoolingDuration(held)}${limit ? ` · ${limit}` : ''}`
}

/** Keep the actual instant chosen by a relative-time button across clock changes. */
export function foodSafetyHeldSinceBefore(minutes, now = moment()) {
  const since = moment(now)
    .seconds(0)
    .milliseconds(0)
    .subtract(minutes, 'minutes')
  return {
    heldSinceTime: since.format('HH:mm'),
    heldSinceAt: since.toISOString(),
  }
}

const HOLD_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/
const HOLD_LONGEST_MINUTES = 24 * 60

/**
 * "Hot-held since" HH:mm on today → ISO + minutes to now. A time later than
 * now means last night. Mirrors Express, which works out the real minutes.
 * @returns {{ heldSinceAt: string|null, minutes: number|null, error: string|null }}
 */
export function resolveFoodSafetyHeldSince({
  localDate,
  heldSinceTime,
  heldSinceAt,
  now = moment(),
}) {
  const empty = { heldSinceAt: null, minutes: null, error: null }
  if (!heldSinceTime) return empty
  if (!HOLD_TIME_RE.test(String(heldSinceTime))) {
    return { ...empty, error: 'Enter a valid holding start time (HH:mm).' }
  }
  const current = moment(now).seconds(0).milliseconds(0)
  let since = heldSinceAt ? moment(heldSinceAt) : null
  if (since && (!since.isValid() || since.format('HH:mm') !== heldSinceTime)) {
    return { ...empty, error: 'Check the holding start time.' }
  }
  if (!since) {
    const format = 'YYYY-MM-DD HH:mm'
    since = moment(`${localDate} ${heldSinceTime}`, format, true)
    if (!since.isValid() || since.format('HH:mm') !== heldSinceTime) {
      return {
        ...empty,
        error:
          'This clock time does not exist on this date — check the start time.',
      }
    }
    if (since.isAfter(current)) since.subtract(1, 'day')
    const wallTime = since.format(format)
    const wallUtc = moment.utc(wallTime, format, true).valueOf()
    const offsets = new Set([
      since.clone().subtract(1, 'day').utcOffset(),
      since.utcOffset(),
      since.clone().add(1, 'day').utcOffset(),
    ])
    const candidates = [...offsets]
      .map(offset => moment(wallUtc - offset * 60000))
      .filter(
        candidate =>
          candidate.format(format) === wallTime && !candidate.isAfter(current),
      )
      .sort((a, b) => a.valueOf() - b.valueOf())
    if (candidates.length > 1) {
      return {
        ...empty,
        error:
          'This time occurred twice when the clocks changed. Choose which start time you mean.',
        startOptions: candidates.map((candidate, index) => ({
          heldSinceAt: candidate.toISOString(),
          label: `${index === 0 ? 'First' : 'Second'} ${heldSinceTime} (${
            index === 0 ? 'before' : 'after'
          } clocks changed)`,
        })),
      }
    }
    if (!candidates.length) {
      return {
        ...empty,
        error:
          'This clock time does not exist on this date — check the start time.',
      }
    }
    since = candidates[0]
  }
  if (since.isAfter(current)) {
    return { ...empty, error: 'Holding start must be before the reading.' }
  }
  const minutes = Math.round(current.diff(since, 'minutes', true))
  if (minutes > HOLD_LONGEST_MINUTES) {
    return {
      ...empty,
      error: 'Held since is more than 24 hours ago — check the time.',
    }
  }
  return { heldSinceAt: since.toISOString(), minutes, error: null }
}

/** "Held 2 hr 10 min — over the 2 hr limit" or null when within / no limit. */
export function formatFoodSafetyHeldTooLong(heldMinutes, maxMinutes) {
  const held = toLimit(heldMinutes)
  const max = toLimit(maxMinutes)
  if (held == null || max == null || held <= max) return null
  return `Held ${formatCoolingDuration(
    held,
  )} — over the ${formatCoolingDuration(max)} limit`
}

/** Corrective panel headline — same words as the saved exception. */
export function formatFoodSafetyHeldTimeHeadline(heldMinutes, maxMinutes) {
  const held = toLimit(heldMinutes)
  const max = toLimit(maxMinutes)
  if (held == null || max == null) return '⚠ Held too long'
  return `⚠ Held ${held} min — over the ${max} min limit`
}

/** Log line: "Held 1 hr 30 min" (+ " · over the 2 hr limit"). */
export function formatFoodSafetyHeldLine(payload, configSnapshot) {
  const held = toLimit(payload?.heldMinutes)
  const since = payload?.heldSinceAt ? moment(payload.heldSinceAt) : null
  const started = since?.isValid()
    ? `Holding started ${since.format('YYYY-MM-DD HH:mm')}`
    : ''
  if (held == null) return started
  const max = toLimit(configSnapshot?.maxHoldingMinutes)
  const over = max != null && held > max
  const elapsed = `Held ${formatCoolingDuration(held)}${
    over ? ` · over the ${formatCoolingDuration(max)} limit` : ''
  }`
  return [started, elapsed].filter(Boolean).join(' · ')
}
